<template>
	<div class="receipt-manager">
		<AppBottomSheet :z-index="1025" :force-width="transactionsBottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="receipt-manager-content" v-if="active">
                <ModalHeader
                    :title="getString('pos_receipt')" />
                <div class="pos-receipt-container">
                    <AppList :items="items">
                        <template v-slot:chevron-slot="{item}">
                            <div class="row centered space-between row-clickable chevron-slot" @click="item.active = !item.active">
                                <p class="list-item-title">{{ item.title }}</p>
                                <div class="chevron-container">
                                    <AppChevronIcon :direction="item.active ? 'up' : 'down'" />
                                </div>
                            </div>
                        </template>
                        <template v-slot:email-dropdown="{item}">
                            <div class="email-container">
                                <div class="ml-auto"><AppTextInput :placeholder="getString('email')" ref="email-receipt" type="email" /></div>
                                <div class="ml-auto mt-10"><AppButton :text="getString('send')" @click="sendEmailReceipt" :loading="loadingSendEmail" /></div>
                            </div>
                        </template>
                    </AppList>
                </div>
            </div>
		</AppBottomSheet>
		<AppNotification :title="getString('error')" :body-text="getString('invalid_email')" ref="email-error" :type="-2" />
		<AppNotification :title="getString('error')" :body-text="getString('generic_error')" ref="generic-error" :type="-2" />
	</div>
</template>

<script>
import {useLanguageStore} from "@/shared/stores";
import {useOrdersActionsStore, useOrdersStore, useUserStore} from "@/console-new/stores";
import AppList from "@/shared/components/AppList.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppButton from "@/shared/components/AppButton.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNotification from "@/shared/components/AppNotification.vue";

import {bold, fixedRightColumn, justifyCenter, lineSeparator} from "@/shared/helpers/escpos";
import {formatDateTimeWithSeconds, getLocalDateFromServer} from "@/shared/helpers/dates";
import {validateEmail} from "@/shared/helpers/strings";

import API from "@/shared/helpers/API";
import {getNative, isAndroid, isNative} from "@/shared/helpers/webapp";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const ordersActionsStore = useOrdersActionsStore();
		const ordersStore = useOrdersStore();
		return {getString: languageStore.getString, userStore, ordersActionsStore, ordersStore};
	},
	name: "ReceiptManager",
	components: {AppNotification, AppTextInput, AppChevronIcon, AppButton, ModalHeader, AppBottomSheet, AppList},
	mixins: [WindowWidth],
	emits: ['success', 'toggling-popup'],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		item: {
			type: Object,
			default: null,
		},
		paymentId: {
			type: String,
			default: null,
		},
	},
	data() {
		return {
			loadingSendEmail: false,
			loadingPrint: false,
		};
	},
	computed: {
		modeNativePrint() {
			if(isAndroid()) {
				console.log("MANUFACTURER "+Android.getManufacturer());
				if(Android.getManufacturer() === "SUNMI" && Android.getDevice) {
					let device = Android.getDevice().toLowerCase();
					console.log("DEVICE "+device);
					let deviceWithPrinters = ["v1", "v1s", "v2", "v2s", "v2pro", "t1mini", "t2mini", "d2mini", "t2", "s2", "m2"];
					if(device && deviceWithPrinters.some(d => device.startsWith(d))) {
						return true;
					}
				}
			}
			return false;
		},
		transactionsBottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		items() {
			let items = [
				{title: this.getString('print_receipt'), buttonTitle: this.getString('print'), buttonLoading: this.loadingPrint, callbackButton: () => this.printReceipt()},
			];

			if(!!this.paymentId || !!this.item || (this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1)) {
				items.push({title: this.getString('email_receipt'), customSlot: 'chevron-slot', active: false, dropdownSlot: 'email-dropdown'});
			}

			return items;
		},
		isNative() {
			return isNative();
		},
	},
	methods: {
		printReceipt() {

			if(this.modeNativePrint && this.item) {

				let printerColumns = 32;

				let result = justifyCenter(bold("Ricevuta di pagamento POS"));
				result += justifyCenter(bold(this.userStore.currBusiness.name));
				result += justifyCenter(bold("Powered by QROMO"))+"\n";

				result += fixedRightColumn(printerColumns, !!this.item.receipt_account_type ? this.item.receipt_account_type : "-", "Tipologia", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_application_cryptogram ? this.item.receipt_application_cryptogram : "-", "App.Crypt.", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_application_preferred_name ? this.item.receipt_application_preferred_name : "-", "App.", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_authorization_code ? this.item.receipt_authorization_code : "-", "Aut.", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_authorization_response_code ? this.item.receipt_authorization_response_code : "-", "Aut.Resp", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_cardholder_verification_method ? this.item.receipt_cardholder_verification_method : "-", "Met.Verif.", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_dedicated_file_name ? this.item.receipt_dedicated_file_name : "-", "File", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_terminal_verification_results ? this.item.receipt_terminal_verification_results : "-", "Verif.Res.", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.receipt_transaction_status_information ? this.item.receipt_transaction_status_information : "-", "Stato", false, true);
				result += fixedRightColumn(printerColumns, !!this.item.payment_intent_id ? this.item.payment_intent_id : "-", "Rif.Qromo", false, true);
				result += lineSeparator(printerColumns);
				let date = getLocalDateFromServer(this.item.date);
				result += fixedRightColumn(printerColumns, "Data e Ora", formatDateTimeWithSeconds(date, false), false, true);
				result += fixedRightColumn(printerColumns, "Importo", (!!this.item.amount ? (Number(this.item.amount)/100).toFixed(2) : "-")+" EUR", false, true);
				result += "\n"
				result += justifyCenter(bold("Grazie e Arrivederci"));
				result += "\n\n\n\n"

				console.log("PRINTING RECEIPT");
				if(isNative() && getNative().print) {
					getNative().print(result);
				}
			} else {

				this.loadingPrint = true;

				//very smelly code :(
				let id = null;
				if(this.item) {
					id = this.item.transaction_id;
				} else if(this.paymentId) {
					id = "p"+this.paymentId;
				} else if(this.ordersStore.selectedOrderGroup) {
					id = "o"+JSON.stringify(this.ordersStore.selectedOrderGroup.orders.map(o => o.order_id));
				}
				if(!id) {
					this.$refs['generic-error'].openNotification();
					return;
				}

				this.ordersActionsStore.printPosReceipts(id, () => {
					this.loadingPrint = false;
				});
			}

		},
		sendEmailReceipt() {

			let email = this.$refs['email-receipt'].currentValue;
			if(!validateEmail(email)) {
				this.$refs['email-error'].openNotification();
			} else {

				//very smelly code :(
				let id = null;
				if(this.item) {
					id = this.item.transaction_id;
				} else if(this.paymentId) {
					id = "p"+this.paymentId;
				} else if(this.ordersStore.selectedOrderGroup && this.ordersStore.selectedOrderGroup.orders.length === 1) {
					id = "o"+this.ordersStore.selectedOrderGroup.orders[0].order_id;
				}
				if(!id) {
					this.$refs['generic-error'].openNotification();
					return;
				}
				this.loadingSendEmail = true;
				API.init().sendTransactionReceiptEmail(this.userStore.currBusinessId, email, id, (r) => {
					this.loadingSendEmail = false;
					if(r && r.result && r.result === "OK") {
						this.$refs['email-receipt'].currentValue = "";
						this.$emit("toggling-popup", false);
						this.$emit("success");
					} else {
						this.$refs['generic-error'].openNotification();
					}
				});

			}

		},
	},
}
</script>

<style scoped lang="scss">
.ml-auto {
	margin-left: auto;
}

.chevron-slot .list-item-title {
	width: calc(100% - 20px);
}
.email-container {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: center;
	width: 100%;

}

.row-clickable {
	cursor: pointer;

	.chevron-container {
		height: 35px;
		display: flex;
		align-items: center;
	}
}
</style>